import { useEffect, useState } from "react";
import axiosInstance from "@/lib/axiosInstance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { LoadingSpinner, SectionLoader } from "@/components/LoadingSpinner";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";

// Define the schema for student assignment
const studentSchema = z.object({
  name: z.string().min(1, "Name is required"),
  age: z.number().min(1, "Age is required"),
  class: z.string().min(1, "Class is required"),
});

const progressSchema = z.object({
  progressDetails: z.string().min(1, "Progress details are required"),
});

const DonorsSection = () => {
  const [donors, setDonors] = useState([]);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedDonorDetails, setSelectedDonorDetails] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState({
    donors: false,
    assignStudent: false,
    updateProgress: false,
  });

  const {
    register: registerStudent,
    handleSubmit: handleSubmitStudent,
    reset: resetStudent,
  } = useForm({
    resolver: zodResolver(studentSchema),
  });

  const {
    register: registerProgress,
    handleSubmit: handleSubmitProgress,
    reset: resetProgress,
  } = useForm({
    resolver: zodResolver(progressSchema),
  });

  const fetchDonors = async () => {
    try {
      setLoading((prev) => ({ ...prev, donors: true }));
      const response = await axiosInstance.get("/admin/donors");
      setDonors(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching donors:", error);
      toast.error("Failed to fetch donors");
    } finally {
      setLoading((prev) => ({ ...prev, donors: false }));
    }
  };

  useEffect(() => {
    fetchDonors();
  }, []);

  const onSubmitStudent = async (data) => {
    try {
      setLoading((prev) => ({ ...prev, assignStudent: true }));
      await axiosInstance.post(`/admin/assign-student`, {
        donorId: selectedDonor._id,
        studentData: data,
      });
      toast.success("Student assigned successfully");
      resetStudent();
      setSelectedDonor(null);
      fetchDonors();
    } catch (error) {
      toast.error("Failed to assign student");
      console.error("Error assigning student:", error);
    } finally {
      setLoading((prev) => ({ ...prev, assignStudent: false }));
    }
  };

  const onSubmitProgress = async (data) => {
    try {
      setLoading((prev) => ({ ...prev, updateProgress: true }));
      await axiosInstance.put(`/admin/update-student-progress`, {
        studentId: selectedStudent._id,
        progressData: {
          details: data.progressDetails,
        },
      });
      toast.success("Progress updated successfully");
      resetProgress();
      setSelectedStudent(null);
      fetchDonors();
    } catch (error) {
      toast.error("Failed to update progress");
      console.error("Error updating progress:", error);
    } finally {
      setLoading((prev) => ({ ...prev, updateProgress: false }));
    }
  };

  const handleDownloadExcel = () => {
    if (!donors || donors.length === 0) {
      toast.error("No donors data available to download");
      return;
    }

    try {
      // Prepare data for Excel export
      const data = donors.map((donor) => {
        // Get first donation details if available (for address, etc.)
        const firstDonation = donor.donations && donor.donations.length > 0 ? donor.donations[0] : null;
        
        // Format all donations
        const allDonationAmounts = donor.donations && donor.donations.length > 0
          ? donor.donations.map((d: any) => d.amount || "").join(", ")
          : "";
        
        const allDonationFrequencies = donor.donations && donor.donations.length > 0
          ? donor.donations.map((d: any) => d.frequency || "").join(", ")
          : "";
        
        const allDonationDates = donor.donations && donor.donations.length > 0
          ? donor.donations.map((d: any) => 
              d.createdAt 
                ? new Date(d.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })
                : ""
            ).join(", ")
          : "";

        // Format students
        const studentNames = donor.students && donor.students.length > 0
          ? donor.students.map((s: any) => `${s.name || ""} (Age: ${s.age || "N/A"}, Class: ${s.class || "N/A"})`).join("; ")
          : "";

        // Format total donation amount with currency
        const totalAmount = donor.totalDonationAmount || 0;
        
        return {
          "First Name": donor.firstName || "",
          "Last Name": donor.lastName || "",
          "Full Name": `${donor.firstName || ""} ${donor.lastName || ""}`.trim(),
          "Email": donor.email || "",
          "Phone": donor.phone || "",
          "Donor Type": donor.donorType || "",
          "Address": firstDonation?.address || "",
          "City": firstDonation?.city || "",
          "State": firstDonation?.state || "",
          "Zip Code": firstDonation?.zipCode || "",
          "Country": firstDonation?.country || "",
          "Pan Card Number": firstDonation?.panCard || "",
          "Total Donation Amount": totalAmount,
          "Total Donation Amount (₹)": `₹${totalAmount.toLocaleString('en-IN')}`,
          "Number of Donations": donor.donations?.length || 0,
          "All Donation Amounts": allDonationAmounts,
          "All Donation Frequencies": allDonationFrequencies,
          "All Donation Dates": allDonationDates,
          "Number of Students": donor.students?.length || 0,
          "Assigned Students": studentNames,
          "Created At": donor.createdAt
            ? new Date(donor.createdAt).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : "",
          "Last Login": donor.lastLogin
            ? new Date(donor.lastLogin).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : "Never",
        };
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 20 }, // Full Name
        { wch: 30 }, // Email
        { wch: 15 }, // Phone
        { wch: 15 }, // Donor Type
        { wch: 35 }, // Address
        { wch: 15 }, // City
        { wch: 15 }, // State
        { wch: 10 }, // Zip Code
        { wch: 15 }, // Country
        { wch: 15 }, // Pan Card Number
        { wch: 20 }, // Total Donation Amount
        { wch: 22 }, // Total Donation Amount (₹)
        { wch: 18 }, // Number of Donations
        { wch: 25 }, // All Donation Amounts
        { wch: 25 }, // All Donation Frequencies
        { wch: 40 }, // All Donation Dates
        { wch: 18 }, // Number of Students
        { wch: 50 }, // Assigned Students
        { wch: 25 }, // Created At
        { wch: 25 }, // Last Login
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Donors Report");

      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0];
      const fileName = `donors_report_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, fileName);
      toast.success("Donors data downloaded successfully");
    } catch (error) {
      console.error("Error downloading donors data:", error);
      toast.error("Failed to download donors data");
    }
  };

  if (loading.donors && donors.length === 0) {
    return <SectionLoader />;
  }

  return (
    <Card className="border-0 shadow-lg rounded-none ">
      <CardHeader className="border-b border-ngo-color6 pb-4">
        <div className="flex justify-between items-center">
          <CardTitle className="text-2xl font-heading text-ngo-rakau">
            Donors
          </CardTitle>
          {donors && donors.length > 0 && (
            <Button
              onClick={handleDownloadExcel}
              className="bg-ngo-color2 hover:bg-ngo-color6 text-white font-semibold px-6 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="overflow-x-auto">
          <Table className="min-w-full divide-y divide-ngo-color6">
            <TableHeader>
              <TableRow className="">
                <TableHead className="px-4 py-2 text-left text-ngo-rakau">
                  Name
                </TableHead>
                <TableHead className="px-4 py-2 text-left text-ngo-rakau">
                  Email
                </TableHead>
                <TableHead className="px-4 py-2 text-left text-ngo-rakau">
                  Phone
                </TableHead>
                <TableHead className="px-4 py-2 text-left text-ngo-rakau">
                  Donor Type
                </TableHead>
                <TableHead className="px-4 py-2 text-left text-ngo-rakau">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-ngo-color6">
              {donors.map((donor) => (
                <TableRow key={donor._id} className="">
                  <TableCell className="px-4 py-2 text-ngo-rakau">
                    {donor.firstName} {donor.lastName}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-ngo-rakau">
                    {donor.email}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-ngo-rakau">
                    {donor.phone}
                  </TableCell>
                  <TableCell className="px-4 py-2 text-ngo-rakau">
                    {donor.donorType}
                  </TableCell>
                  <TableCell className="px-4 py-2">
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedDonorDetails(donor);
                          setIsViewDialogOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                      <Dialog open={isViewDialogOpen && selectedDonorDetails?._id === donor._id} onOpenChange={(open) => {
                        if (!open) {
                          setIsViewDialogOpen(false);
                          setSelectedDonorDetails(null);
                        }
                      }}>
                        {selectedDonorDetails && selectedDonorDetails._id === donor._id && (
                          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto text-ngo-rakau">
                            <DialogHeader>
                              <DialogTitle className="text-2xl font-heading text-ngo-rakau">
                                Details for {selectedDonorDetails.firstName}{" "}
                                {selectedDonorDetails.lastName}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-6 pr-2">
                              <div className="space-y-4 border rounded-lg p-4">
                                <h4 className="text-lg font-semibold text-ngo-rakau border-b border-ngo-rakau pb-2">
                                  Personal Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-sm font-medium text-gray-700">Email</Label>
                                    <p className="text-sm text-ngo-rakau mt-1 break-all">
                                      {selectedDonorDetails.email}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-medium text-gray-700">Phone</Label>
                                    <p className="text-sm text-ngo-rakau mt-1">
                                      {selectedDonorDetails.phone}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-medium text-gray-700">Donor Type</Label>
                                    <p className="text-sm text-ngo-rakau mt-1">
                                      {selectedDonorDetails.donorType}
                                    </p>
                                  </div>
                                  {selectedDonorDetails.donations && selectedDonorDetails.donations.length > 0 && (
                                    <>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">Address</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {selectedDonorDetails.donations[0].address || 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">City</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {selectedDonorDetails.donations[0].city || 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">State</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {selectedDonorDetails.donations[0].state || 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">Zip Code</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {selectedDonorDetails.donations[0].zipCode || 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">Country</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {selectedDonorDetails.donations[0].country || 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">Pan Card Number</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {selectedDonorDetails.donations[0].panCard || 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-gray-700">Created At</Label>
                                        <p className="text-sm text-ngo-rakau mt-1">
                                          {new Date(selectedDonorDetails.createdAt).toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </p>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="space-y-4 border rounded-lg p-4">
                                <h4 className="text-lg font-semibold text-ngo-rakau border-b border-ngo-rakau pb-2">
                                  Donation Information
                                </h4>
                                <div>
                                  <Label className="text-sm font-medium text-gray-700">Total Donation Amount</Label>
                                  <p className="text-lg font-semibold text-ngo-rakau mt-1">
                                    ₹{selectedDonorDetails.totalDonationAmount?.toLocaleString('en-IN') || '0'}
                                  </p>
                                </div>
                                {selectedDonorDetails.donations && selectedDonorDetails.donations.length > 0 && (
                                  <div className="space-y-3 mt-4">
                                    <Label className="text-sm font-medium text-gray-700">Donation History</Label>
                                    {selectedDonorDetails.donations.map(
                                      (donation, index) => (
                                        <div
                                          key={index}
                                          className="p-4 border border-ngo-rakau rounded-lg bg-gray-50"
                                        >
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                              <Label className="text-xs font-medium text-gray-600">Amount</Label>
                                              <p className="text-sm font-semibold text-ngo-rakau mt-1">
                                                ₹{donation.amount?.toLocaleString('en-IN') || '0'}
                                              </p>
                                            </div>
                                            <div>
                                              <Label className="text-xs font-medium text-gray-600">Frequency</Label>
                                              <p className="text-sm text-ngo-rakau mt-1">
                                                {donation.frequency || 'N/A'}
                                              </p>
                                            </div>
                                            <div>
                                              <Label className="text-xs font-medium text-gray-600">Date</Label>
                                              <p className="text-sm text-ngo-rakau mt-1">
                                                {new Date(
                                                  donation.createdAt
                                                ).toLocaleDateString('en-US', {
                                                  year: 'numeric',
                                                  month: 'long',
                                                  day: 'numeric'
                                                })}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <DialogFooter>
                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setIsViewDialogOpen(false);
                                  setSelectedDonorDetails(null);
                                }}
                              >
                                Close
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        )}
                      </Dialog>

                      <Dialog>
                        <DialogTrigger asChild>
                          {
                            (!(donor.donorType == "Regular" && donor.donations.every(donation => donation.frequency == "once"))) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedDonor(donor)}
                              >
                                Assign Student
                              </Button>
                            )
                          }

                        </DialogTrigger>
                        {selectedDonor?._id === donor._id && (
                          <DialogContent className="text-ngo-rakau">
                            <DialogHeader>
                              <DialogTitle className="text-2xl font-heading text-ngo-rakau">
                                Assign Student to {donor.firstName}
                              </DialogTitle>
                            </DialogHeader>
                            <form
                              onSubmit={handleSubmitStudent(onSubmitStudent)}
                              className="space-y-4"
                            >
                              <div>
                                <Label
                                  htmlFor="name"
                                  className="text-ngo-rakau"
                                >
                                  Name
                                </Label>
                                <Input
                                  id="name"
                                  {...registerStudent("name")}
                                  className="border-ngo-rakau text-ngo-rakau"
                                />
                              </div>
                              <div>
                                <Label htmlFor="age" className="text-ngo-rakau">
                                  Age
                                </Label>
                                <Input
                                  id="age"
                                  type="number"
                                  {...registerStudent("age", {
                                    valueAsNumber: true,
                                  })}
                                  className="border-ngo-rakau text-ngo-rakau"
                                />
                              </div>
                              <div>
                                <Label
                                  htmlFor="class"
                                  className="text-ngo-rakau"
                                >
                                  Class
                                </Label>
                                <Input
                                  id="class"
                                  {...registerStudent("class")}
                                  className="border-ngo-rakau text-ngo-rakau"
                                />
                              </div>
                              <Button
                                type="submit"
                                disabled={loading.assignStudent}
                              >
                                {loading.assignStudent ? (
                                  <>
                                    <LoadingSpinner
                                      size="sm"
                                      className="mr-2"
                                    />
                                    Assigning...
                                  </>
                                ) : (
                                  "Assign"
                                )}
                              </Button>
                            </form>
                          </DialogContent>
                        )}
                      </Dialog>
                      {donor.students && donor.students.length > 0 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedDonor(donor)}
                            >
                              View Students
                            </Button>
                          </DialogTrigger>
                          {selectedDonor?._id === donor._id && (
                            <DialogContent className="max-w-3xl  text-ngo-rakau">
                              <DialogHeader>
                                <DialogTitle className="text-2xl font-heading text-ngo-rakau">
                                  Students of {donor.firstName}
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                {selectedDonor.students.map((student) => (
                                  <div
                                    key={student._id}
                                    className="p-4 border border-ngo-rakau rounded-lg cursor-pointer flex justify-between "
                                    onClick={() => setSelectedStudent(student)}
                                  >
                                    <h4 className="font-medium text-ngo-rakau">
                                      {student.name}
                                    </h4>
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="text-ngo-color4 hover:text-ngo-color4/80"
                                        >
                                          View Details
                                        </Button>
                                      </DialogTrigger>
                                      {selectedStudent?._id === student._id && (
                                        <DialogContent className=" text-ngo-rakau">
                                          <DialogHeader>
                                            <DialogTitle className="text-2xl font-heading text-ngo-rakau">
                                              Progress for {student.name}
                                            </DialogTitle>
                                          </DialogHeader>
                                          <form
                                            onSubmit={handleSubmitProgress(
                                              onSubmitProgress
                                            )}
                                            className="space-y-4"
                                          >
                                            <div>
                                              <Label
                                                htmlFor="progressDetails"
                                                className="text-ngo-rakau"
                                              >
                                                Progress Details
                                              </Label>
                                              <Textarea
                                                id="progressDetails"
                                                {...registerProgress(
                                                  "progressDetails"
                                                )}
                                                className="border-ngo-rakau text-ngo-rakau"
                                              />
                                            </div>
                                            <Button
                                              type="submit"
                                              className="bg-ngo-color4 text-ngo-rakau hover:bg-ngo-color4/90"
                                              disabled={loading.updateProgress}
                                            >
                                              {loading.updateProgress ? (
                                                <>
                                                  <LoadingSpinner
                                                    size="sm"
                                                    className="mr-2"
                                                  />
                                                  Updating...
                                                </>
                                              ) : (
                                                "Update Progress"
                                              )}
                                            </Button>
                                          </form>
                                          <div>
                                            <h5 className="font-medium mt-4 text-ngo-rakau">
                                              Progress History:
                                            </h5>
                                            {student.progress &&
                                              student.progress.map(
                                                (progress, index) => (
                                                  <div
                                                    key={index}
                                                    className="mt-2 p-2 border border-ngo-rakau rounded-lg"
                                                  >
                                                    <p className="text-ngo-rakau">
                                                      <strong>Date:</strong>{" "}
                                                      {new Date(
                                                        progress.date
                                                      ).toLocaleDateString()}
                                                    </p>
                                                    <p className="text-ngo-rakau">
                                                      <strong>Details:</strong>{" "}
                                                      {progress.details}
                                                    </p>
                                                  </div>
                                                )
                                              )}
                                          </div>
                                        </DialogContent>
                                      )}
                                    </Dialog>
                                  </div>
                                ))}
                              </div>
                            </DialogContent>
                          )}
                        </Dialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default DonorsSection;
